import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

type Cmd = { label: string; to: string; hint?: string };

// Ctrl/⌘-K command palette: navigation + live asset search (real data via RLS).
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [assets, setAssets] = useState<{ id: string; name: string; qr_token: string }[]>([]);
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [tiers, setTiers] = useState<{ id: string; display_name: string; roles: string[] | null }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const navCmds: Cmd[] = [
    { label: t('wo.title'), to: '/work-orders' },
    { label: t('nav.dueDates'), to: '/due-dates' },
    { label: t('nav.assets'), to: '/assets' },
    { label: t('nav.properties'), to: '/properties' },
    { label: t('nav.clients'), to: '/clients' },
    { label: t('nav.users'), to: '/users' },
    { label: t('nav.organization'), to: '/settings' },
    { label: t('nav.help'), to: '/help' },
  ];

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      setAssets([]); setProps([]); setTiers([]);
      return;
    }
    let active = true;
    // live search across modules (below) + records (fiches): assets, properties,
    // and the unified tiers directory — all through RLS.
    void Promise.all([
      supabase.from('assets').select('id,name,qr_token').ilike('name', `%${needle}%`).is('deleted_at', null).limit(5),
      supabase.from('properties').select('id,name').ilike('name', `%${needle}%`).is('deleted_at', null).limit(5),
      supabase.from('directory').select('id,display_name,roles').ilike('display_name', `%${needle}%`).limit(5),
    ]).then(([a, p, d]) => {
      if (!active) return;
      setAssets(a.data ?? []);
      setProps(p.data ?? []);
      setTiers((d.data as any) ?? []);
    });
    return () => {
      active = false;
    };
  }, [q]);

  const filteredNav = useMemo(
    () => navCmds.filter((c) => !q.trim() || c.label.toLowerCase().includes(q.toLowerCase())),
    [q, navCmds],
  );

  if (!open) return null;
  const go = (to: string) => {
    nav(to);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[12vh]" onClick={onClose}>
      <div className="w-full max-w-[560px] overflow-hidden rounded-card bg-card shadow-card" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          placeholder={t('common.searchPlaceholder')}
          className="h-12 w-full border-b border-divider bg-card px-4 text-md outline-none"
        />
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {filteredNav.map((c) => (
            <button key={c.to} onClick={() => go(c.to)} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-bg">
              <span>{c.label}</span>
              <span className="font-mono text-2xs text-muted">↵</span>
            </button>
          ))}
          {assets.length > 0 && (
            <>
              <div className="eyebrow px-3 pb-1 pt-3">{t('nav.assets')}</div>
              {assets.map((a) => (
                <button key={a.id} onClick={() => go(`/assets/${a.id}`)} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-bg">
                  <span>{a.name}</span>
                  <span className="font-mono text-2xs text-muted">{a.qr_token}</span>
                </button>
              ))}
            </>
          )}
          {props.length > 0 && (
            <>
              <div className="eyebrow px-3 pb-1 pt-3">{t('nav.properties')}</div>
              {props.map((p) => (
                <button key={p.id} onClick={() => go(`/properties/${p.id}`)} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-bg">
                  <span>{p.name}</span>
                  <span className="font-mono text-2xs text-muted">↵</span>
                </button>
              ))}
            </>
          )}
          {tiers.length > 0 && (
            <>
              <div className="eyebrow px-3 pb-1 pt-3">{t('nav.groupClientsPartners')}</div>
              {tiers.map((p) => (
                <button key={p.id} onClick={() => go('/clients')} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-bg">
                  <span>{p.display_name}</span>
                  <span className="font-mono text-2xs text-muted">{(p.roles ?? []).join(', ')}</span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
